import { useId, useState } from 'react';

import { FOCO_VISIVEL } from 'utils/foco';

import { CartaoDoExtrato as Cartao, formatarPercentual, formatarPontos } from '../extrato-modelo';
import { coresDaLetra } from './cores';
import IconePresente from './IconePresente';
import TooltipDoExtrato from './TooltipDoExtrato';

function Subtitulo({ cartao }: { cartao: Cartao }) {
  if (cartao.formula) {
    const { base, rotuloBase, multiplicador, rotuloMultiplicador } = cartao.formula;
    return (
      <p className="m-0 mt-[7px] text-[12px] text-content-soft">
        <b className="font-bold text-brand-blue">{formatarPontos(base)}</b> {rotuloBase}{' '}
        <span aria-label="multiplicado por" className="mx-1 text-brand-blue">
          ×
        </span>{' '}
        {rotuloMultiplicador} <b className="font-bold text-brand-blue">{multiplicador ?? 0}</b>
      </p>
    );
  }
  if (cartao.descricao) return <p className="m-0 mt-[7px] text-[12px] text-content-soft">{cartao.descricao}</p>;
  return null;
}

/**
 * Cartão da composição do extrato (PREM-06 a PREM-10): letra ou ícone, título com tooltip,
 * fórmula ou subtítulo, valor (ou o aviso que o substitui), chip de percentual e "Ver mais".
 */
export default function CartaoDoExtrato({ cartao }: { cartao: Cartao }) {
  const [aberto, setAberto] = useState(false);
  const idDoPainel = useId();
  const temVerMais = !!cartao.detalhes?.length || !!cartao.detalheGaleria;
  const cor = cartao.letra ? coresDaLetra(cartao.letra) : undefined;

  return (
    <li
      tabIndex={0}
      className={`rounded-xl bg-brand-white px-5 py-5 shadow-jj-01 transition duration-[180ms] hover:-translate-y-[3px] hover:shadow-[0_12px_26px_rgba(0,45,75,0.13)] motion-reduce:transition-none motion-reduce:hover:translate-y-0 sm:px-6 ${FOCO_VISIVEL}`}
    >
      <div className="flex flex-wrap items-center gap-x-[18px] gap-y-3 sm:flex-nowrap">
        {cor ? (
          <span
            aria-hidden="true"
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-[17px] font-extrabold text-brand-blue-dark ${cor.forte}`}
          >
            {cartao.letra}
          </span>
        ) : (
          <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#EFF1F4] text-content-soft">
            <IconePresente tamanho={19} espessura={2.1} />
          </span>
        )}

        <div className="min-w-0 flex-1 basis-[calc(100%-58px)] sm:basis-0">
          <div className="group/tip relative inline-flex items-center gap-[7px]">
            <h3 className="m-0 font-display text-[15px] font-bold leading-[normal] text-content">
              {cartao.letra && <span className="sr-only">{cartao.letra} · </span>}
              {cartao.titulo}
            </h3>
            {cartao.tooltip && <TooltipDoExtrato id={cartao.tooltip} titulo={cartao.titulo} />}
          </div>
          <Subtitulo cartao={cartao} />
        </div>

        <div className="ml-[58px] flex shrink-0 items-center gap-[18px] sm:ml-0">
          {cartao.aviso ? (
            <div className="max-w-[320px] text-right text-[12px] leading-[1.5] text-content">
              {cartao.aviso.map(linha => (
                <p key={linha} className="m-0">
                  {linha}
                </p>
              ))}
            </div>
          ) : (
            <p className="m-0 text-right">
              <span className={`block font-display text-[26px] font-extrabold ${cartao.sinal ? 'text-content-soft' : 'text-brand-blue-dark'}`}>
                {cartao.sinal && `${cartao.sinal} `}
                {formatarPontos(cartao.valor)}
              </span>
              <span className="block text-[11px] text-content-soft">{cartao.unidade}</span>
            </p>
          )}
          {cor && (
            <span
              className={`w-[62px] shrink-0 rounded-full py-[5px] text-center text-[11px] font-bold text-brand-blue-dark ${
                typeof cartao.percentual === 'number' ? cor.clara : ''
              }`}
            >
              {typeof cartao.percentual === 'number' ? formatarPercentual(cartao.percentual) : ''}
            </span>
          )}
          {!cor && <span aria-hidden="true" className="w-[62px] shrink-0" />}
        </div>
      </div>

      {temVerMais && (
        <>
          <div id={idDoPainel} hidden={!aberto}>
            {cartao.detalheGaleria && (
              <div className="mt-[18px] flex flex-wrap items-center justify-between gap-3 rounded-lg bg-[#F6F7F9] px-[15px] py-[13px]">
                <p className="m-0 text-[12px] text-content">{cartao.detalheGaleria.descricao}</p>
                <p className="m-0 font-display text-[15px] font-bold text-brand-blue-dark">{cartao.detalheGaleria.pontos}</p>
              </div>
            )}
            {!!cartao.detalhes?.length && (
              <ul className="m-0 grid list-none grid-cols-1 gap-2.5 p-0 pb-1 pt-[18px] sm:grid-cols-2 lg:grid-cols-4">
                {cartao.detalhes.map(item => (
                  <li key={item.chave} className="rounded-lg bg-[#F6F7F9] px-[15px] py-[13px]">
                    <p className="m-0 mb-[5px] text-[11px] text-content-soft">{item.titulo}</p>
                    {item.periodo && <p className="m-0 mb-[5px] text-[11px] text-content-soft">{item.periodo}</p>}
                    <p className="m-0 font-display text-[17px] font-bold text-brand-blue-dark">{item.pontos}</p>
                    {item.nivel && <p className="m-0 mt-1.5 text-[11px] text-content">{item.nivel}</p>}
                    <p className="m-0 mt-1 text-[11px] text-content">{item.realizacao}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            aria-expanded={aberto}
            aria-controls={idDoPainel}
            onClick={() => setAberto(valor => !valor)}
            className={`mt-3.5 flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 font-text text-[11px] font-bold uppercase tracking-[0.04em] text-brand-blue transition-colors hover:text-brand-blue-dark ${FOCO_VISIVEL}`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true" focusable="false" className={`transition-transform duration-300 motion-reduce:transition-none ${aberto ? 'rotate-45' : ''}`}>
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            {aberto ? 'Ver menos' : 'Ver mais'}
            <span className="sr-only"> sobre {cartao.titulo}</span>
          </button>
        </>
      )}
    </li>
  );
}
